import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Loader2, History, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { ApplyTargetRateDialog } from './ApplyTargetRateDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtA = (v) => `A$${Math.abs(Math.round(v)).toLocaleString()}`;
const SCOPE = {
  sell: { cls: 'bg-[#0F2040] text-white', dot: 'bg-[#0F2040]' },
  regional: { cls: 'bg-sky-100 text-sky-800', dot: 'bg-sky-500' },
  cost: { cls: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
};

/** Compact vertical timeline of rate-card changes and the A$ margin swing each implies on today's open deals. */
export function RateHistoryTimelineCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(8);
  const [revert, setRevert] = useState(null);
  const { user } = useAuth();
  const canRevert = ['cro', 'cco', 'super_admin'].includes(user?.role);
  const canRevertCost = ['admin', 'super_admin'].includes(user?.role);

  const load = () => axios.get(`${API_URL}/api/insights/margin/rate-history`, { params: { limit: 40 }, withCredentials: true })
    .then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Could not load rate history'));
  useEffect(() => { load(); }, []);

  const revertCost = async (dl) => {
    if (!window.confirm(`Revert ${dl.region ? dl.region + ' ' : ''}${dl.role} internal cost rate to A$${Number(dl.before).toFixed(0)}/h?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/role-pricing/cost-rates`, { rates: { [dl.role]: dl.before }, region: dl.region || null }, { withCredentials: true });
      toast.success(r.data.message); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not revert'); }
  };
  const RevertLink = ({ e, dl, idx }) => {
    const isCost = e.kind === 'cost';
    if (!dl.revertible || (isCost ? !canRevertCost : !canRevert)) return null;
    const toGlobal = !isCost && dl.region && dl.before == null;
    return (
      <button type="button" onClick={() => (isCost ? revertCost(dl) : setRevert({ role: dl.role, rate: toGlobal ? dl.global_rate : dl.before, region: dl.region || '', revertToGlobal: toGlobal }))}
        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#4ABFCF] hover:text-[#0F2040] hover:underline ml-1"
        title={toGlobal ? `Remove the ${dl.region} override so ${dl.role} inherits the global rate again` : `Revert ${dl.role} to ${Number(dl.before).toFixed(0)} with the same preview and rep alerts`}
        data-testid={`rate-history-revert-${idx}-${dl.region || 'global'}-${dl.role}`}>
        <Undo2 className="w-3 h-3" /> Revert{toGlobal ? ' to global' : ` to ${Number(dl.before).toFixed(0)}`}
      </button>
    );
  };

  const entries = (data?.entries || []).slice(0, limit);
  return (
    <Card className="border-slate-200" data-testid="rate-history-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <h3 className="text-sm font-semibold text-[#0F2040] flex items-center gap-1.5"><History className="w-4 h-4 text-slate-400" /> Rate-card history</h3>
            <p className="text-[11px] text-slate-400">Every sell, regional and internal-cost rate change, with the margin swing it implies across {data?.open_deals ?? '—'} open deals at today's hours (A$).</p>
          </div>
        </div>
        {error && <p className="text-xs text-rose-600" data-testid="rate-history-error">{error}</p>}
        {!data && !error && <div className="flex items-center gap-2 text-xs text-slate-400 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
        {data && data.entries.length === 0 && <p className="text-xs text-slate-400" data-testid="rate-history-empty">No rate-card changes recorded yet.</p>}
        {entries.length > 0 && (
          <ol className="relative mt-3 ml-2 border-l border-slate-200 space-y-3" data-testid="rate-history-timeline">
            {entries.map((e, i) => {
              const sc = SCOPE[e.kind] || SCOPE.sell;
              const up = e.impact_aud > 0.5; const down = e.impact_aud < -0.5;
              return (
                <li key={`${e.at}-${i}`} className="relative pl-4" data-testid={`rate-history-entry-${i}`}>
                  <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${sc.dot}`} />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                    <span className="text-slate-400 tabular-nums">{new Date(e.at).toLocaleDateString()}</span>
                    <span className={`text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ${sc.cls}`}>{e.scope}</span>
                    <span className="font-medium text-[#0F2040]" data-testid={`rate-history-summary-${i}`}>{e.summary}</span>
                    <span className="text-slate-400">by {e.updated_by || '—'}{e.source === 'role_pricing_target' ? ' · target rate' : e.source === 'role_pricing_card' ? ' · cost gap' : ''}</span>
                    {(e.deltas || []).map((dl) => <RevertLink key={`${dl.region || 'g'}-${dl.role}`} e={e} dl={dl} idx={i} />)}
                  </div>
                  <div className="text-[11px] mt-0.5" title={e.impact_basis} data-testid={`rate-history-impact-${i}`}>
                    <span className={`font-semibold tabular-nums ${up ? 'text-emerald-700' : down ? 'text-rose-600' : 'text-slate-400'}`}>
                      {up ? '+' : down ? '−' : ''}{fmtA(e.impact_aud)} margin
                    </span>
                    <span className="text-slate-400"> on {e.impact_hours.toLocaleString()}h of open-deal work{e.affected != null ? ` · ${e.affected} deal(s) at the time` : ''}{e.note ? ` · “${e.note}”` : ''}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {revert && (
          <ApplyTargetRateDialog open role={revert.role} rate={revert.rate} initialRegion={revert.region} revertToGlobal={revert.revertToGlobal} canApply={canRevert}
            title="Revert rate change" onClose={() => setRevert(null)} onApplied={() => load()} />
        )}
        {data && data.entries.length > limit && (
          <button type="button" onClick={() => setLimit((l) => l + 8)} className="mt-2 text-[11px] text-[#4ABFCF] hover:underline" data-testid="rate-history-more-btn">Show older changes</button>
        )}
      </CardContent>
    </Card>
  );
}
